import type { JSX } from 'react';

import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';

import { BaseFontFeatureSettings } from 'DesignSystem2/Text';
import Palette from 'DesignSystem2/Theme/Palette';
import Spacing from 'DesignSystem2/Theme/Spacing';

interface TooltipProps {
    children: JSX.Element;
    title: string | JSX.Element;
    open?: boolean;
    childComponentIsClickable?: boolean;
    disableHoverListener?: boolean;
    enterDelay?: number;
    leaveDelay?: number;

    tooltipStyle?: any;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    /** px gap between the trigger and the tooltip. Defaults to 4. */
    offset?: number;
    classname?: string;
    onOpen?: () => void;
    onClose?: () => void;
}

const TooltipComponent = ({
    children,
    title,
    open,
    onOpen,
    onClose,
    childComponentIsClickable = false,
    tooltipStyle,
    placement,
    offset = 4,
    classname,
    disableHoverListener,
    enterDelay,
    leaveDelay,
}: TooltipProps): JSX.Element => {
    const gap = `${offset}px`;
    const controlledOpenProps = open !== undefined && {
        disableFocusListener: true,
        disableHoverListener: true,
        disableTouchListener: true,
        open,
        onOpen,
        onClose,
    };

    return (
        <Tooltip
            // Hack to Style tooltip on app router pages.
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: Palette.Fill.Inverse,
                        color: Palette.Text.x10,
                        fontFeatureSettings: BaseFontFeatureSettings,
                        fontSize: '13px',
                        lineHeight: '20px',
                        letterSpacing: '0.13px',
                        fontWeight: 500,
                        padding: `${Spacing(1)} ${Spacing(1.5)}`,
                        fontFamily: 'var(--font-kalshi-sans)',
                        border: `1px solid ${Palette.Fill.x40}`,
                        boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.10)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        '&.MuiTooltip-tooltip': {
                            '&.MuiTooltip-tooltipPlacementBottom': {
                                marginTop: gap,
                            },
                            '&.MuiTooltip-tooltipPlacementTop': {
                                marginBottom: gap,
                            },
                            '&.MuiTooltip-tooltipPlacementLeft': {
                                marginRight: gap,
                            },
                            '&.MuiTooltip-tooltipPlacementRight': {
                                marginLeft: gap,
                            },
                        },
                        ...tooltipStyle,
                    },
                    onClick: (e) => {
                        e.stopPropagation();
                    },
                },
            }}
            placement={placement}
            title={title}
            enterDelay={enterDelay}
            enterTouchDelay={!childComponentIsClickable ? 0 : undefined}
            disableHoverListener={disableHoverListener}
            leaveDelay={leaveDelay}
            leaveTouchDelay={5000}
            {...controlledOpenProps}
        >
            <span className={clsx(classname, 'select-none [&_*]:select-none cursor-pointer')}>{children}</span>
        </Tooltip>
    );
};

export default TooltipComponent;
